import graphqlClient from "@/GQLClient";
import {
  type CategoryInput,
  SaveCategoryDocument,
  Category_Type,
  GetNewsCategoriesDocument,
  DeleteCategoryDocument,
  GetNewsCountOnRelatedCategoryDocument,
} from "@/graphql/generated-types";
import { useAuthStore } from "@/stores/auth.module";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { CategoryFragment } from "@/graphql";
import type { ICategoryAPI } from "@/interfaces";

const getCategories = async (): Promise<CategoryFragment[]> => {
  const authStore = useAuthStore();
  const institutionId = authStore.institutionId;

  const response = await graphqlClient.query({
    query: GetNewsCategoriesDocument,
    variables: {
      institutionId,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.newsItem?.getCategories) {
    return response.data.newsItem.getCategories;
  }

  throw new SentryGraphQLError(`api.newsCategories.getCategories`, response);
};

const save = async (category: CategoryInput) => {
  const response = await graphqlClient.query({
    query: SaveCategoryDocument,
    variables: {
      category,
      type: Category_Type.News,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.category?.save?.success) {
    return response.data.category.save;
  }

  throw new SentryGraphQLError(`api.newsCategories.save`, response);
};

const deleteItem = async (id: string) => {
  const response = await graphqlClient.query({
    query: DeleteCategoryDocument,
    variables: {
      id,
      type: Category_Type.News,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.category?.delete?.success) {
    return response.data.category.delete;
  }

  throw new SentryGraphQLError(`api.newsCategories.deleteItem`, response);
};

const getEntityCountOnRelatedCategory = async (
  categoryId: number,
): Promise<number> => {
  const authStore = useAuthStore();
  const institutionId = authStore.institutionId;

  const response = await graphqlClient.query({
    query: GetNewsCountOnRelatedCategoryDocument,
    variables: {
      institutionId,
      ids: [categoryId.toString()],
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (
    response.data.newsItem?.getCategories &&
    response.data.newsItem.getCategories.length
  ) {
    const [category] = response.data.newsItem.getCategories;
    return category?.entities?.totalCount || 0;
  }

  throw new SentryGraphQLError(
    `api.newsCategories.getEntityCountOnRelatedCategory`,
    response,
  );
};

export default {
  getCategories,
  save,
  deleteItem,
  getEntityCountOnRelatedCategory,
} as ICategoryAPI;
